import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Progress } from "./ui/progress";
import { AlertTriangle, TrendingUp, CheckCircle2, X, Target } from "lucide-react";

const SEVERITY_WEIGHT = { high: 25, medium: 12, low: 6 };
const SEVERITY_COLOR = { high: "rose", medium: "amber", low: "slate" };
const SEVERITY_LABEL = { high: "Yüksek", medium: "Orta", low: "Düşük" };

/**
 * RiskExplainerDrawer — açıklanabilir AI: risk skoru nasıl hesaplandı?
 * Sinyal listesi, ağırlık katkıları ve önerilen aksiyonlar.
 */
export function RiskExplainerDrawer({ open, onClose, employee, risk }) {
  if (!risk) return null;
  const signals = risk.signals || [];
  const totalWeight = signals.reduce((s, x) => s + (SEVERITY_WEIGHT[x.severity] || 0), 0);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="risk-explainer-drawer">
        <SheetHeader className="space-y-1">
          <div className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-wider font-semibold text-rose-700">
            <AlertTriangle className="w-3.5 h-3.5" />
            Ayrılma Risk Skoru — Nasıl Hesaplandı?
          </div>
          <SheetTitle className="font-display text-2xl">
            {employee?.name} · %{risk.risk_score}
          </SheetTitle>
          <SheetDescription className="text-sm text-slate-600">
            Risk skoru, çalışanın çevresel ve davranışsal sinyallerinin ağırlıklı toplamı ile hesaplanır.
            Yüksek şiddet sinyali +25 puan, orta +12, düşük +6 puan katkı sağlar (üst sınır %95).
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          {/* Risk gauge */}
          <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs font-semibold text-rose-800 uppercase tracking-wider">Risk Düzeyi</span>
              <span className="text-3xl font-bold tabular-nums text-rose-700">%{risk.risk_score}</span>
            </div>
            <Progress value={risk.risk_score} className="h-2 [&>div]:bg-rose-500" />
            <div className="text-[0.7rem] text-rose-700 mt-2">
              Sinyal toplam katkısı: +{totalWeight} puan · Şiddet: <strong>{risk.risk_level === "high" ? "Yüksek" : risk.risk_level === "medium" ? "Orta" : "Düşük"}</strong>
            </div>
          </div>

          {/* Signals */}
          <div>
            <div className="text-[0.7rem] uppercase tracking-wider font-semibold text-slate-500 mb-2">
              Tetikleyici Sinyaller ({signals.length})
            </div>
            <div className="space-y-2">
              {signals.length === 0 && (
                <div className="text-sm text-slate-500 italic p-3 bg-slate-50 rounded-lg">
                  Aktif risk sinyali yok — düşük baz puanı çalışanın bağlılık skorundan türetildi.
                </div>
              )}
              {signals.map((s, idx) => {
                const color = SEVERITY_COLOR[s.severity] || "slate";
                const weight = SEVERITY_WEIGHT[s.severity] || 0;
                return (
                  <div
                    key={idx}
                    className={`flex items-start gap-3 p-3 rounded-lg border bg-${color}-50/60 border-${color}-200`}
                    data-testid={`risk-signal-${idx}`}
                  >
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center bg-${color}-100 text-${color}-700 shrink-0`}>
                      <AlertTriangle className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800">{s.label || s.description || s.signal}</div>
                      <div className="text-[0.7rem] text-slate-500 mt-0.5">
                        Şiddet: <strong>{SEVERITY_LABEL[s.severity]}</strong> · Katkı: <strong>+{weight} puan</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recommended actions */}
          {risk.recommended_actions?.length > 0 && (
            <div>
              <div className="text-[0.7rem] uppercase tracking-wider font-semibold text-slate-500 mb-2">
                Önerilen Aksiyonlar
              </div>
              <ul className="space-y-1.5">
                {risk.recommended_actions.map((a, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="pt-3 border-t border-slate-100 text-[0.65rem] text-slate-400 italic">
            Plena AI · Açıklanabilir model · Bu hesaplama Türkçe sinyal listesi üzerinde ağırlıklı puanlama yapar.
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * ReadinessExplainerDrawer — hazır olma skoru açıklaması.
 * Hedef rol gereksinimleri vs çalışan seviyesi karşılaştırması.
 */
export function ReadinessExplainerDrawer({ open, onClose, employee, gap }) {
  if (!gap) return null;
  const aligned = gap.aligned || [];
  const gaps = gap.gaps || [];
  const exceeds = gap.exceeds || [];
  const total = gap.total_required || (aligned.length + gaps.length + exceeds.length);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="readiness-explainer-drawer">
        <SheetHeader className="space-y-1">
          <div className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-wider font-semibold text-sky-700">
            <Target className="w-3.5 h-3.5" />
            Hazır Olma Skoru — Nasıl Hesaplandı?
          </div>
          <SheetTitle className="font-display text-2xl">
            {employee?.name} · %{gap.readiness_score}
          </SheetTitle>
          <SheetDescription className="text-sm text-slate-600">
            Hazır olma skoru, hedef rol için gereken her becerinin <strong>ağırlıklı seviye eşleşmesi</strong> ile hesaplanır.
            Çalışan seviyesi ÷ gereken seviye × beceri ağırlığı; toplamın gereken toplama oranı.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          {/* Score gauge */}
          <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-4">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs font-semibold text-sky-800 uppercase tracking-wider">Genel Hazır Olma</span>
              <span className="text-3xl font-bold tabular-nums text-sky-700">%{gap.readiness_score}</span>
            </div>
            <Progress value={gap.readiness_score} className="h-2 [&>div]:bg-sky-500" />
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <div className="bg-emerald-50 rounded-md p-2">
                <div className="text-lg font-bold text-emerald-700">{aligned.length}</div>
                <div className="text-[0.6rem] uppercase text-emerald-600">Uyumlu</div>
              </div>
              <div className="bg-amber-50 rounded-md p-2">
                <div className="text-lg font-bold text-amber-700">{gaps.length}</div>
                <div className="text-[0.6rem] uppercase text-amber-600">Eksik</div>
              </div>
              <div className="bg-sky-50 rounded-md p-2">
                <div className="text-lg font-bold text-sky-700">{exceeds.length}</div>
                <div className="text-[0.6rem] uppercase text-sky-600">Aşan</div>
              </div>
            </div>
            <div className="text-[0.7rem] text-sky-700 mt-2">
              Toplam gereken yetkinlik: <strong>{total}</strong>
            </div>
          </div>

          {/* Gaps */}
          {gaps.length > 0 && (
            <div>
              <div className="text-[0.7rem] uppercase tracking-wider font-semibold text-amber-700 mb-2">
                Açık Veren Beceriler ({gaps.length})
              </div>
              <div className="space-y-2">
                {gaps.slice(0, 6).map((g, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2.5 rounded-lg border border-amber-100 bg-amber-50/40">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{g.skill_name}</div>
                      <div className="text-[0.7rem] text-slate-500">
                        Mevcut: <strong>sv{g.current_level || 0}</strong> · Hedef: <strong>sv{g.required_level}</strong>
                      </div>
                    </div>
                    <div className="text-amber-700 text-sm font-bold tabular-nums">−{g.delta}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Aligned (top 4) */}
          {aligned.length > 0 && (
            <div>
              <div className="text-[0.7rem] uppercase tracking-wider font-semibold text-emerald-700 mb-2">
                Uyumlu Beceriler ({aligned.length})
              </div>
              <div className="grid grid-cols-2 gap-2">
                {aligned.slice(0, 6).map((a, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 rounded-md bg-emerald-50/60 border border-emerald-100">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span className="text-xs text-slate-700 truncate">{a.skill_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-3 border-t border-slate-100 text-[0.65rem] text-slate-400 italic">
            Plena AI · Açıklanabilir model · Her açık becerinin tamamlanması için tahmini 3 ay öğrenme süresi öngörülür.
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
